import React from 'react'
import { useTranslation } from 'react-i18next'
import { LoaderCircle, Search } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'

const ALL = '__all__'

export type BudgetViewMode = 'budget' | 'contracts'

interface Props {
  levelOne: { id: string; name: string }[]
  rootId: string | null
  onRootChange: (id: string | null) => void
  search: string
  onSearchChange: (q: string) => void
  saving: boolean
  actions?: React.ReactNode
  viewMode: BudgetViewMode
  onViewModeChange: (mode: BudgetViewMode) => void
}

/**
 * Filters left, actions right (urkla-ui toolbar pattern). The filters stay on one line from `sm` up; on a
 * narrow screen the whole action group drops to the next line instead of the inputs stacking.
 */
const BudgetToolbar: React.FC<Props> = ({ levelOne, rootId, onRootChange, search, onSearchChange, saving, actions, viewMode, onViewModeChange }) => {
  const { t } = useTranslation()
  return (
    <div className="flex flex-wrap items-center gap-2 py-2">
      <div className="flex min-w-0 flex-wrap items-center gap-2 sm:flex-nowrap">
        <Tabs value={viewMode} onValueChange={(v) => onViewModeChange(v as BudgetViewMode)}>
          <TabsList className="h-8">
            <TabsTrigger value="budget" className="h-6 text-sm">{t('CostControl_view_budget')}</TabsTrigger>
            <TabsTrigger value="contracts" className="h-6 text-sm">{t('CostControl_view_contracts')}</TabsTrigger>
          </TabsList>
        </Tabs>
        {viewMode === 'budget' && (
          <>
          <Select value={rootId ?? ALL} onValueChange={(v) => onRootChange(v === ALL ? null : v)}>
            <SelectTrigger className="h-8 w-auto min-w-[180px] max-w-[260px] text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t('CostControl_filter_all_level_one')}</SelectItem>
              {levelOne.map((n) => <SelectItem key={n.id} value={n.id}>{n.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={search} onChange={(e) => onSearchChange(e.target.value)} placeholder={t('CostControl_search_placeholder')} className="h-8 w-[220px] pl-8 text-sm" />
          </div>
          </>
        )}
        {saving && viewMode === 'budget' && (
          <span className="flex shrink-0 items-center gap-1 text-xs text-muted-foreground">
            <LoaderCircle className="h-3.5 w-3.5 animate-spin" />
            {t('CostControl_saving')}
          </span>
        )}
      </div>
      {actions && <div className="ml-auto flex items-center gap-2">{actions}</div>}
    </div>
  )
}
export default BudgetToolbar
